import React, {FC, lazy, ReactElement, Suspense, useEffect, useState} from 'react';
import NavBar from './components/NavBar';
import {Route, Routes} from 'react-router-dom';
import Loading from './components/Loading';
import {Grid} from '@mui/material';
import logo_white from './logo_white.png';
import './MainApp.scss';
import './components/broadcast/BroadcastDialog.scss';
import SnackbarToast from './components/errors/SnackbarToast';
import {LoginInfo} from './contexts/Configuration';
import {saveUserInfo} from './reducers/accessReducer';
import {getBalance} from './reducers/billingReducer';
import {useAppDispatch} from './hooks/storeHooks';
import {useIsAuthenticated, useMsal} from '@azure/msal-react';
import useExecWithToken from './utils/FetchUtils';
import {InteractionRequiredAuthError, InteractionStatus} from '@azure/msal-browser';
import {jwtDecode} from 'jwt-decode';
import {IdTokenPayload} from './utils/IdTokenPayload';
import {loginRequest} from './msalConfig';
import TosDialog, {getTosAccepted} from './components/tos/Tos';

const Loadable = (Component: any) => (props: JSX.IntrinsicAttributes) => (
    <Suspense fallback={<Loading/>}>
        <Component {...props}/>
    </Suspense>
)

const Billing = Loadable(lazy(() => import ("./pages/Billing")));
const Transcription = Loadable(lazy(() => import ("./pages/Transcription")));
const BackOffice = Loadable(lazy(() => import ("./pages/BackOffice")));
const Help = Loadable(lazy(() => import ("./pages/Help")));
const ContactUs = Loadable(lazy(() => import ("./pages/ContactUs")));


const OperatorApp: FC<OperatorAppProps> = ({}): ReactElement => {

    const [loading, setLoading] = useState<boolean>(true)
    const [tosAccepted, setTosAccepted] = useState<boolean>(false)

    const dispatch = useAppDispatch();
    const {instance, accounts, inProgress} = useMsal();
    let isAuthenticated = useIsAuthenticated();

    const execWithToken = useExecWithToken();


    useEffect(() => {
        //console.log('[LOGIN] Authentication status', isAuthenticated, inProgress)

        if (inProgress === InteractionStatus.Logout) {
            //console.log('[LOGIN] Logout... do nothing')
            return;
        }

        if (!isAuthenticated) {
            if (accounts.length > 0) {
                //console.log('[LOGIN] Accounts.length > 0')
                instance.acquireTokenSilent({...loginRequest, account: accounts[0]})
                    .then((result) => {
                        //console.log('[LOGIN] acquireTokenSilent successful -> ', result, isAuthenticated, inProgress)
                        let activeAccount = instance.getAllAccounts().length > 0 ? instance.getAllAccounts()[0] : undefined;
                        if (activeAccount === undefined || activeAccount === null) {
                            throw new InteractionRequiredAuthError('No account available');
                        }
                    })
                    .catch((error) => {
                        //console.log('[LOGIN] login redirect because of err -> ', error)
                        instance.loginRedirect(loginRequest)
                    })
            } else {
                //console.log('[LOGIN] accounts.length <= 0')
                instance.loginRedirect(loginRequest)
            }
            return;
        }

        //console.log('[LOGIN] Executing with token userInfo and TosAccepted...')

        execWithToken((token) => {
            let activeAccount = accounts[0];
            const decode = jwtDecode<IdTokenPayload>(token.replace("Bearer ", ""));
            const roles = decode.roles;
            const loginInfo: LoginInfo = {account: activeAccount, token: token, roles: roles};

            dispatch(saveUserInfo(loginInfo));

            getTosAccepted(token, activeAccount.username)
                .then(tosAccepted => {
                    setTosAccepted(tosAccepted)
                    setLoading(false)
                })

            if (tosAccepted)
                dispatch(getBalance({token: token}))
        });

    }, [tosAccepted, isAuthenticated, accounts]);

    if (loading) {
        return (
            <div className="loading-screen">
                <img src={logo_white} alt="Company Logo" className="logo"/>
            </div>
        );
    }

    if (!tosAccepted) {
        return <TosDialog onTosAccepted={accepted => setTosAccepted(accepted)}/>
    }

    return (
        <Grid container spacing={0} className='main-container'>
            {/*NavBar*/}
            <Grid item xs={1} md={2} className='nav-bar-component'>
                <NavBar/>
            </Grid>
            {/*Body*/}
            <Grid item xs={11} md={10} className='content-container'>
                <div className='route-container'>
                    <Routes>
                        <Route path='/' element={<SnackbarToast><Transcription/></SnackbarToast>}/>
                        <Route path='/billing' element={<SnackbarToast><Billing/></SnackbarToast>}/>
                        <Route path='/backoffice' element={<SnackbarToast><BackOffice/></SnackbarToast>}/>
                        <Route path='/help' element={<SnackbarToast><Help/></SnackbarToast>}/>
                        <Route path='/contactUs' element={<SnackbarToast><ContactUs/></SnackbarToast>}/>
                    </Routes>
                </div>
            </Grid>
        </Grid>
    )
}


type OperatorAppProps = {}

export default OperatorApp;
